import type { AircraftRoute } from '@/types';

// ---------------------------------------------------------------------------
// Aircraft Route Lookup — adsbdb.com (무료, 키 불필요, CORS 허용)
//
// ADS-B 전파 자체에는 목적지가 없다. 편명(callsign)으로 노선 DB 를 조회해야
// 출발/도착 공항을 알 수 있다. adsb.lol 의 routeset 엔드포인트는 현재
// content-length 0 으로 응답하여 사용 불가 → adsbdb 를 쓴다.
//
// 커버리지는 100% 가 아니다 (군용기·전세기·포지셔닝 편은 404). 없으면 null.
// ---------------------------------------------------------------------------

interface AdsbdbAirport {
  iata_code?: string;
  icao_code?: string;
  municipality?: string;
  name?: string;
  country_name?: string;
}

interface AdsbdbResponse {
  response?: {
    flightroute?: {
      callsign?: string;
      airline?: { name?: string };
      origin?: AdsbdbAirport;
      destination?: AdsbdbAirport;
    };
  };
}

/** 편명 → 노선. 조회 실패/미등록이면 null. */
const cache = new Map<string, AircraftRoute | null>();
const inflight = new Map<string, Promise<AircraftRoute | null>>();

export function getCachedRoute(callsign: string): AircraftRoute | null | undefined {
  return cache.get(normalise(callsign));
}

function normalise(callsign: string): string {
  return callsign.trim().toUpperCase();
}

export async function fetchAircraftRoute(callsign: string): Promise<AircraftRoute | null> {
  const cs = normalise(callsign);
  // 편명이 아니라 hex/등록번호면 조회 의미 없음
  if (!cs || cs.length < 3 || !/^[A-Z]{2,3}\d/.test(cs)) return null;

  if (cache.has(cs)) return cache.get(cs) ?? null;
  const pending = inflight.get(cs);
  if (pending) return pending;

  const task = (async (): Promise<AircraftRoute | null> => {
    try {
      const res = await fetch(`https://api.adsbdb.com/v0/callsign/${encodeURIComponent(cs)}`, {
        signal: AbortSignal.timeout(8_000),
      });
      if (!res.ok) { cache.set(cs, null); return null; }

      const json: AdsbdbResponse = await res.json();
      const fr = json.response?.flightroute;
      if (!fr?.origin && !fr?.destination) { cache.set(cs, null); return null; }

      const route: AircraftRoute = {
        callsign: fr.callsign ?? cs,
        airline: fr.airline?.name,
        originIata: fr.origin?.iata_code,
        originName: fr.origin?.name,
        originCity: fr.origin?.municipality,
        destIata: fr.destination?.iata_code,
        destName: fr.destination?.name,
        destCity: fr.destination?.municipality,
      };
      cache.set(cs, route);
      return route;
    } catch {
      // 네트워크 실패는 캐시하지 않는다 (다음에 재시도)
      return null;
    } finally {
      inflight.delete(cs);
    }
  })();

  inflight.set(cs, task);
  return task;
}
